import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, Minimize2, Settings } from 'lucide-react'
import { PDFDocument } from 'pdf-lib'
import toast from 'react-hot-toast'
import Dropdown from '../components/ui/Dropdown'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface CompressionOptions {
  quality: 'low' | 'medium' | 'high'
  removeMetadata: boolean
  optimizeImages: boolean
}

interface PDFFile {
  id: string
  file: File
  name: string
  pageCount: number
  originalSize: number
  compressedSize?: number
  compressedBlob?: Blob
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  compressionRatio?: number
}

const PDFCompressor: React.FC = () => {
  const [files, setFiles] = useState<PDFFile[]>([])
  const [options, setOptions] = useState<CompressionOptions>({
    quality: 'medium',
    removeMetadata: true,
    optimizeImages: true
  })

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf'
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select valid PDF files')
      return
    }

    const newFiles: PDFFile[] = []
    
    for (const file of pdfFiles) {
      try {
        const arrayBuffer = await file.arrayBuffer()
        const pdfDoc = await PDFDocument.load(arrayBuffer)
        const pageCount = pdfDoc.getPageCount()
        
        newFiles.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          name: file.name,
          pageCount,
          originalSize: file.size,
          status: 'pending',
          progress: 0
        })
      } catch (error) {
        newFiles.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          name: file.name,
          pageCount: 0,
          originalSize: file.size,
          status: 'error',
          progress: 0
        })
        toast.error(`Failed to load ${file.name}`)
      }
    }

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${newFiles.filter(f => f.status === 'pending').length} PDF(s) for compression`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  })

  const compressPDF = async (pdfFile: PDFFile): Promise<Blob> => {
    const arrayBuffer = await pdfFile.file.arrayBuffer()
    const pdfDoc = await PDFDocument.load(arrayBuffer)
    
    // Remove metadata if requested
    if (options.removeMetadata) {
      pdfDoc.setTitle('')
      pdfDoc.setAuthor('')
      pdfDoc.setSubject('')
      pdfDoc.setCreator('')
      pdfDoc.setProducer('')
      pdfDoc.setCreationDate(new Date())
      pdfDoc.setModificationDate(new Date())
    }
    
    // Save with compression options
    const pdfBytes = await pdfDoc.save({
      useObjectStreams: true,
      addDefaultPage: false,
      objectsPerTick: options.quality === 'high' ? 100 : options.quality === 'medium' ? 50 : 25
    })
    
    return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })
  }

  const processFiles = async () => {
    const pendingFiles = files.filter(file => file.status === 'pending')
    
    for (const file of pendingFiles) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 20 } : f
        ))

        // Simulate processing progress
        for (let progress = 40; progress <= 80; progress += 20) {
          await new Promise(resolve => setTimeout(resolve, 300))
          setFiles(prev => prev.map(f => 
            f.id === file.id ? { ...f, progress } : f
          ))
        }

        const compressedBlob = await compressPDF(file)
        const compressionRatio = ((file.originalSize - compressedBlob.size) / file.originalSize) * 100
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { 
            ...f, 
            compressedBlob,
            compressedSize: compressedBlob.size,
            compressionRatio,
            status: 'completed',
            progress: 100
          } : f
        ))

        toast.success(`Compressed ${file.name} (${compressionRatio.toFixed(1)}% reduction)`)
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to compress ${file.name}`)
      }
    }
  }

  const downloadFile = (file: PDFFile) => {
    if (!file.compressedBlob) return

    const url = URL.createObjectURL(file.compressedBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.split('.')[0]}_compressed.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const downloadAll = () => {
    const completedFiles = files.filter(f => f.status === 'completed' && f.compressedBlob)
    completedFiles.forEach((file, index) => {
      setTimeout(() => downloadFile(file), index * 100)
    })
  }

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const totalOriginalSize = files.reduce((sum, file) => sum + file.originalSize, 0)
  const totalCompressedSize = files.reduce((sum, file) => sum + (file.compressedSize || 0), 0)
  const overallCompressionRatio = totalOriginalSize > 0 ? ((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100 : 0

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF Compressor
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Reduce PDF file sizes while maintaining document quality. Remove metadata and optimize structure with advanced compression.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfCompressor.header}
          className="mb-8"
        />

        {/* Options Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-4">
            <Settings className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Compression Options</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Compression Quality
              </label>
              <Dropdown
                options={[
                  {
                    value: 'high',
                    label: 'High Quality',
                    description: 'Less compression, better quality'
                  },
                  {
                    value: 'medium',
                    label: 'Medium Quality',
                    description: 'Balanced compression and quality'
                  },
                  {
                    value: 'low',
                    label: 'Low Quality',
                    description: 'Maximum compression, smaller files'
                  }
                ]}
                value={options.quality}
                onChange={(value) => setOptions(prev => ({ ...prev, quality: value as 'low' | 'medium' | 'high' }))}
                placeholder="Select quality level"
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={options.removeMetadata}
                  onChange={(e) => setOptions(prev => ({ ...prev, removeMetadata: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-gray-700">Remove metadata</span>
              </label>
            </div>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={options.optimizeImages}
                  onChange={(e) => setOptions(prev => ({ ...prev, optimizeImages: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-gray-700">Optimize images</span>
              </label>
            </div>
          </div>
        </motion.div>

        {/* Upload Area */}
        <div
          {...getRootProps()}
          className={`card border-2 border-dashed cursor-pointer transition-colors mb-8 ${
            isDragActive ? 'border-[#8438ff] bg-[#8438ff]/10' : 'border-gray-300 hover:border-[#8438ff]'
          }`}
        >
          <input {...getInputProps()} />
          <div className="text-center py-12">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-xl font-medium text-[#8438ff] mb-2">
              {isDragActive ? 'Drop PDF files here' : 'Choose PDFs or drag them here'}
            </p>
            <p className="text-gray-600">
              Select PDF files to compress and reduce their file size
            </p>
          </div>
        </div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfCompressor.content}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-[#8438ff]">Files to Compress ({files.length})</h3>
                {totalOriginalSize > 0 && (
                  <p className="text-sm text-gray-600">
                    Total size: {formatFileSize(totalOriginalSize)}
                    {totalCompressedSize > 0 && (
                      <> → {formatFileSize(totalCompressedSize)} ({overallCompressionRatio.toFixed(1)}% reduction)</>
                    )}
                  </p>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={processFiles}
                  disabled={!files.some(f => f.status === 'pending')}
                  className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minimize2 className="h-4 w-4 mr-2" />
                  Compress All
                </button>
                
                {files.some(f => f.status === 'completed') && (
                  <button
                    onClick={downloadAll}
                    className="btn btn-success"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download All
                  </button>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {files.map((file) => (
                <div key={file.id} className={`card ${
                  file.status === 'error' ? 'border-red-200 bg-red-50' : ''
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <FileText className={`h-8 w-8 ${
                        file.status === 'error' ? 'text-red-400' : 'text-gray-400'
                      }`} />
                      <div>
                        <p className={`font-medium ${
                          file.status === 'error' ? 'text-red-900' : 'text-[#8438ff]'
                        }`}>
                          {file.name}
                        </p>
                        <p className={`text-sm ${
                          file.status === 'error' ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {file.status === 'error' 
                            ? 'Failed to load' 
                            : `${file.pageCount} pages • ${formatFileSize(file.originalSize)}`
                          }
                          {file.compressedSize && (
                            <> → {formatFileSize(file.compressedSize)} ({file.compressionRatio?.toFixed(1)}% smaller)</>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {file.status === 'processing' && (
                        <div className="flex items-center space-x-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-[#8438ff] h-2 rounded-full transition-all duration-300"
                              style={{ width: `${file.progress}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{file.progress}%</span>
                        </div>
                      )}
                      
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Success Summary */}
        {files.length > 0 && files.every(f => f.status === 'completed') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card bg-green-50 border-green-200"
          >
            <div className="text-center py-6">
              <div className="text-green-600 mb-2">
                <Minimize2 className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-green-900 mb-2">
                Compression Complete!
              </h3>
              <p className="text-green-700 mb-4">
                Successfully compressed {files.length} PDF file(s) with an average reduction of {overallCompressionRatio.toFixed(1)}%.
                Total size reduced from {formatFileSize(totalOriginalSize)} to {formatFileSize(totalCompressedSize)}.
              </p>
              <button
                onClick={downloadAll}
                className="btn btn-success"
              >
                <Download className="h-4 w-4 mr-2" />
                Download All Compressed Files
              </button>
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfCompressor.footer}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PDFCompressor
